/**
 * Billing amount allocation.
 *
 * Each billing step's HT amount is `montant_total * percentage / 100`, which
 * almost never lands on a whole cent (e.g. 7.5% of 27 875 € = 2 090,625 €).
 * Rounding every step independently makes the steps sum to a cent or two off
 * the contract total — so the invoices a client receives don't reconcile with
 * the signed contract (the "30 729,17 vs 30 729,19" gap).
 *
 * `allocatePendingHT` spreads the budget across the *pending* steps with the
 * largest-remainder method: floor every share to the cent, then hand the
 * leftover cent(s) to the steps whose exact share had the largest fractional
 * part. The result is rounded to the cent AND sums EXACTLY to the budget.
 *
 * Processed steps (already invoiced) keep their frozen montant and are not
 * passed here — the budget given is the *remaining* HT (montant_total minus
 * the frozen processed amounts).
 *
 * @param pendingIdx  step indices to allocate (in original step order)
 * @param pendingPct  each pending step's percentage (same order as pendingIdx)
 * @param remainingHT budget to distribute across the pending steps, in euros
 * @returns map stepIndex -> HT amount (euros, 2-decimal), summing to remainingHT
 */
/**
 * Format a euro amount for display, keeping the centimes.
 *
 * The billing data is allocated to the cent (see `allocatePendingHT`), so the
 * UI must never round to whole euros — a 7.5% step worth 2 362,50 € TTC was
 * being shown as "2 363 €", which doesn't reconcile with the signed contract.
 * We round to the cent (to absorb float noise from the ×1.2 TVA factor) and
 * format with up to 2 decimals, matching the project-edit modal.
 */
export function formatEuros(amount: number): string {
  const cents = Math.round(amount * 100) / 100;
  return cents.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

type CpEntry = { id: string; label: string; themeLabel: string; stepLabel: string };

/** Flatten a themes tree into a list of controlpoints with their context. */
function indexControlpoints(themes: any[] | undefined): CpEntry[] {
  const out: CpEntry[] = [];
  for (const theme of themes || []) {
    for (const step of theme?.steps || []) {
      for (const cp of step?.controlpoints || []) {
        if (cp?.id) {
          out.push({
            id: cp.id,
            label: (cp.label || "").trim(),
            themeLabel: (theme.label || "").trim(),
            stepLabel: (step.label || "").trim(),
          });
        }
      }
    }
  }
  return out;
}

/**
 * Resolve a billing step's trigger controlpoint ids against a REAL project.
 *
 * The billing-template mapping stores controlpoint ids taken from a *project
 * template*. A real project's controlpoints often carry DIFFERENT ids than the
 * template the mapping was built against — the project was created from another
 * template (or an older version), ids were regenerated, etc. A raw id match
 * then silently finds nothing, so the step never auto-triggers and the user has
 * to relink by hand ("modifier le point de contrôle déclencheur").
 *
 * Resolution, in order, per mapping id:
 *   1. Keep it if that exact id exists in the project.
 *   2. Otherwise look the id up in the source project template to get its
 *      controlpoint LABEL (+ theme/step context), then find the project
 *      controlpoint with the same theme+step+label; failing that, a unique
 *      same-label controlpoint anywhere in the project.
 *
 * Only ever returns ids that exist in the project, and only resolves by label
 * when the match is unambiguous — so it can't wire a step to the wrong invoice.
 * Returns [] when nothing resolves (caller keeps the step unlinked, as before).
 */
export function resolveLinkedStepIds(
  mappingCpIds: string[] | undefined,
  projectThemes: any[] | undefined,
  templateThemes: any[] | undefined,
): string[] {
  if (!mappingCpIds || mappingCpIds.length === 0) return [];
  const projectCps = indexControlpoints(projectThemes);
  const projectIds = new Set(projectCps.map((c) => c.id));
  const templateById = new Map<string, CpEntry>();
  for (const c of indexControlpoints(templateThemes)) templateById.set(c.id, c);

  const resolved = new Set<string>();
  for (const id of mappingCpIds) {
    if (projectIds.has(id)) {
      resolved.add(id);
      continue;
    }
    const tmpl = templateById.get(id);
    if (!tmpl || !tmpl.label) continue;
    const exact = projectCps.find(
      (c) =>
        c.label === tmpl.label &&
        c.themeLabel === tmpl.themeLabel &&
        c.stepLabel === tmpl.stepLabel,
    );
    let match = exact;
    if (!match) {
      const sameLabel = projectCps.filter((c) => c.label === tmpl.label);
      if (sameLabel.length === 1) match = sameLabel[0];
    }
    if (match) resolved.add(match.id);
  }
  return Array.from(resolved);
}

export function allocatePendingHT(
  pendingIdx: number[],
  pendingPct: number[],
  remainingHT: number,
): Record<number, number> {
  const out: Record<number, number> = {};
  if (pendingIdx.length === 0) return out;

  const totalCents = Math.round(remainingHT * 100);
  const weightSum = pendingPct.reduce((a, b) => a + b, 0);
  if (weightSum <= 0) {
    pendingIdx.forEach((i) => (out[i] = 0));
    return out;
  }

  const raw = pendingPct.map((w) => (totalCents * w) / weightSum);
  const floored = raw.map((c) => Math.floor(c));
  const remainder = totalCents - floored.reduce((a, b) => a + b, 0);

  // Steps with the biggest fractional part get the leftover cents first.
  const order = raw
    .map((c, k) => ({ k, frac: c - Math.floor(c) }))
    .sort((a, b) => b.frac - a.frac);

  const cents = floored.slice();
  for (let n = 0; n < remainder; n++) {
    cents[order[n % order.length].k] += 1;
  }

  pendingIdx.forEach((i, k) => (out[i] = cents[k] / 100));
  return out;
}
